import Flickity from 'flickity';
import { sleep } from './lib/utils.js';

class FFHomeHero extends HTMLElement {
	flkty!: Flickity;
	changeId = 0;

	DOM: {
		dots: HTMLElement[];
		rotator: HTMLElement | null;
		slides: NodeListOf<HTMLElement>;
		textSlides: NodeListOf<HTMLElement>;
		controlsWrapper: HTMLElement | null;
	};

	constructor() {
		super();
		this.DOM = {
			dots: [],
			rotator: this.querySelector('.js-rotator'),
			slides: this.querySelectorAll('.js-slide'),
			textSlides: this.querySelectorAll('.js-textSlide'),
			controlsWrapper: this.querySelector('.controlsWrapper'),
		};
		this.initRotator();
		this.initControls();
		this.lazyLoadSlideBackgrounds();
	}

	get imagesBase(): string {
		return this.dataset.imagesBase || '';
	}

	loadSlideBackground(slide: HTMLElement): void {
		if (slide.classList.contains('js-bg-loaded')) return;

		const mobile = slide.dataset.mobileImage;
		const desktop = slide.dataset.desktopImage;
		if (!mobile || !desktop || !this.imagesBase) return;

		const mobileUrl = `${this.imagesBase}${mobile}`;
		const desktopUrl = `${this.imagesBase}${desktop}`;
		const mobileWebpUrl = mobileUrl.replace(/\.(jpe?g|png)$/i, '.webp');
		const desktopWebpUrl = desktopUrl.replace(/\.(jpe?g|png)$/i, '.webp');
		const useDesktop = window.matchMedia('(min-width: 768px)').matches;

		slide.style.backgroundImage = useDesktop
			? `image-set(url('${desktopWebpUrl}') type('image/webp'), url('${desktopUrl}') type('image/jpeg'))`
			: `image-set(url('${mobileWebpUrl}') type('image/webp'), url('${mobileUrl}') type('image/jpeg'))`;
		slide.classList.add('js-bg-loaded');
	}

	lazyLoadSlideBackgrounds(): void {
		const slides = Array.from(this.DOM.slides);
		const deferredSlides = slides.slice(1);

		const loadDeferred = () => {
			deferredSlides.forEach((slide) => this.loadSlideBackground(slide));
		};

		if ('requestIdleCallback' in window) {
			requestIdleCallback(loadDeferred);
		} else {
			setTimeout(loadDeferred, 2000);
		}

		this.flkty.on('change', (index: number) => {
			this.loadSlideBackground(slides[index]);
			const nextIndex = (index + 1) % slides.length;
			this.loadSlideBackground(slides[nextIndex]);
		});
	}

	async initControls() {
		const controls = document.createElement('div');
		controls.classList.add('rotatorControls');
		this.DOM.controlsWrapper?.appendChild(controls);
		const prev = document.createElement('button');
		prev.type = 'button';
		prev.classList.add('directionButton');
		prev.setAttribute('aria-label', 'Previous slide');
		prev.innerHTML = this.prevIcon;
		controls.appendChild(prev);
		prev.addEventListener('click', this.prevClick.bind(this));
		const list = document.createElement('ul');
		controls.appendChild(list);
		for (let i = 0; i < this.DOM.slides.length; i++) {
			const li = document.createElement('li');
			const dot = document.createElement('button');
			dot.type = 'button';
			li.appendChild(dot);
			dot.addEventListener('click', this.dotClick.bind(this, i));
			dot.classList.add('dot');
			const slideTitle = this.DOM.slides[i]?.dataset.slideTitle || `Slide ${i + 1}`;
			dot.setAttribute('aria-label', `Go to slide ${i + 1}: ${slideTitle}`);
			if (i === 0) {
				li.classList.add('js-active');
			}
			this.DOM.dots.push(li);
			list.appendChild(li);
		}
		const next = document.createElement('button');
		next.type = 'button';
		next.classList.add('directionButton');
		next.setAttribute('aria-label', 'Next slide');
		controls.appendChild(next);
		next.innerHTML = this.nextIcon;
		next.addEventListener('click', this.nextClick.bind(this));
		await sleep(100);
		controls.classList.add('js-active');
	}

	initRotator() {
		if (!this.DOM.rotator) return;
		this.flkty = new Flickity(this.DOM.rotator, {
			pageDots: false,
			prevNextButtons: false,
			wrapAround: true,
			autoPlay: 7000,
			pauseAutoPlayOnHover: false,
		});
		this.flkty.on('change', this.rotatorChange.bind(this));
	}

	resetAutoPlay() {
		this.flkty.stopPlayer();
		this.flkty.playPlayer();
	}

	prevClick() {
		this.flkty.previous();
		this.resetAutoPlay();
	}
	nextClick() {
		this.flkty.next();
		this.resetAutoPlay();
	}
	dotClick(i: number) {
		this.flkty.select(i);
		this.resetAutoPlay();
	}
	async rotatorChange(index: number) {
		const changeId = ++this.changeId;
		const slideCount = Math.min(this.DOM.dots.length, this.DOM.textSlides.length);
		for (let i = 0; i < slideCount; i++) {
			this.DOM.dots[i]?.classList.remove('js-active');
			this.DOM.textSlides[i]?.classList.remove('js-active');
		}
		this.DOM.dots[index]?.classList.add('js-active');
		await sleep(300);
		if (changeId !== this.changeId) return;
		this.DOM.textSlides[index]?.classList.add('js-active');
	}

	nextIcon = `<svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.429688 0.428223L4.21286 4.18573C4.83682 4.80546 4.82313 5.81889 4.18264 6.42154L0.429687 9.95278" stroke="white" stroke-width="0.85631" stroke-linecap="round"/>
</svg>`;
	prevIcon = `<svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.67188 9.95264L0.888706 6.19513C0.26474 5.5754 0.278435 4.56197 0.918918 3.95932L4.67187 0.428079" stroke="white" stroke-width="0.85631" stroke-linecap="round"/>
</svg>`;
}
customElements.define('ff-home-hero', FFHomeHero);
